/**
 * Internal helper that proxies every OCPP host-facing endpoint through the
 * existing `fetchDataFromAPITyped` pipeline so JWT, IP, username headers,
 * and base URL handling stay centralised.
 *
 * All endpoints in `src/ocpp/services/*` MUST go through this helper so we
 * have a single tap point for logging / mocking / retries.
 */

import {
  ApiRequest,
  ApiResponse,
  fetchDataFromAPITyped,
} from '../../services/api-requests';

/** Base path. Mounted under the `charger` "entity" so existing fetcher logic still applies. */
export const OCPP_BASE = 'charger/ocpp';

const buildSearch = (params?: Record<string, string | number | boolean | undefined | null>) => {
  if (!params) return undefined;
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    sp.append(key, String(value));
  });
  return sp.toString().length ? sp : undefined;
};

interface OcppRequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
}

/**
 * Issue an OCPP host-facing request. `path` is everything after `OCPP_BASE`
 * (e.g. `connectors/abc-123` or `security/abc-123/events`). The helper
 * normalises leading slashes and URL-encodes path-segments at the call
 * site (callers are expected to encode dynamic segments themselves so we
 * don't accidentally percent-encode `/`).
 */
export const ocppRequest = async <T>(opts: OcppRequestOptions): Promise<ApiResponse<T | null>> => {
  const path = opts.path.startsWith('/') ? opts.path.slice(1) : opts.path;
  // The existing fetcher only knows about GET/POST/PUT/DELETE; PATCH gets
  // tunneled via POST + `_method=PATCH` query so we don't have to widen the
  // base contract. Backend route uses `app.patch(...)` so this only matters
  // until we extend `ApiRequest['method']`.
  const isPatch = opts.method === 'PATCH';
  const apiRequest: ApiRequest = {
    route: `${OCPP_BASE}/${path}`,
    request: {
      method: isPatch ? 'POST' : opts.method,
      entity: 'charger',
      body: opts.body ?? undefined,
      searchParams: buildSearch({
        ...(opts.query ?? {}),
        ...(isPatch ? { _method: 'PATCH' } : {}),
      }),
    },
  };
  return fetchDataFromAPITyped<T>(apiRequest);
};

/** Encode a single path segment for inclusion in an OCPP route. */
export const enc = (segment: string | number): string => encodeURIComponent(String(segment));
